import React, { useEffect, useCallback, useRef } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import NodeControl from "../components/node-control.js";

/** HangUp node - says a message then ends the call without waiting for response */
const HangUp = ({
  id,
  data,
  selected,
}) => {
  const { updateNodeData } = useReactFlow();

  // Use refs for uncontrolled inputs - prevents cursor jumping
  const titleInputRef = useRef(null);
  const messageRef = useRef(null);

  // Track cursor positions to restore after re-renders
  const titleCursorRef = useRef(0);
  const messageCursorRef = useRef(0);

  // After every render, restore cursor position with a small delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleInputRef.current && titleCursorRef.current > 0) {
        try {
          titleInputRef.current.setSelectionRange(titleCursorRef.current, titleCursorRef.current);
        } catch (e) {
          // Ignore
        }
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (messageRef.current && messageCursorRef.current > 0) {
        try {
          messageRef.current.setSelectionRange(messageCursorRef.current, messageCursorRef.current);
        } catch (e) {
          // Ignore
        }
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  });

  const handleBlur = useCallback(
    (key, value) => {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\s+/g, " ");
      updateNodeData(id, { [key]: normalizedValue });
    },
    [id, updateNodeData]
  );

  return (
    <div
      className={cn(
        "w-[300px] group inline-flex flex-col items-start justify-start rounded-lg border-2 border-red-400/60 bg-[#FFEBEE] shadow-sm",
        selected && "outline-2 outline-red-500/70"
      )}
    >
      <Handle className="!top-5" type="target" position={Position.Left} />

      <header className="flex w-full items-center justify-start gap-2 px-3 pb-2 pt-3">
        <span className="shrink-0 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          Hang Up
        </span>
        <Input
          ref={titleInputRef}
          className="nodrag nowheel border-red-200/80 bg-white/90"
          type="text"
          defaultValue={data.title || ""}
          placeholder="Label"
          onKeyDown={(evt) => {
            evt.stopPropagation();
            if (titleInputRef.current) {
              titleCursorRef.current = titleInputRef.current.selectionStart || 0;
            }
          }}
          onKeyUp={(evt) => {
            evt.stopPropagation();
            if (titleInputRef.current) {
              titleCursorRef.current = titleInputRef.current.selectionStart || 0;
            }
          }}
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("title", evt.target.value)}
        />
        <NodeControl id={id} selected={selected} />
      </header>

      <div className="flex w-full flex-col items-start justify-start gap-0.5 p-1.5">
        <p className="px-1 text-[11px] text-red-800/80">
          Say this message, then end the call. Don't wait for user response.
        </p>

        <Textarea
          ref={messageRef}
          defaultValue={data.message || ""}
          onKeyDown={(evt) => {
            evt.stopPropagation();
            if (messageRef.current) {
              messageCursorRef.current = messageRef.current.selectionStart || 0;
            }
          }}
          onKeyUp={(evt) => {
            evt.stopPropagation();
            if (messageRef.current) {
              messageCursorRef.current = messageRef.current.selectionStart || 0;
            }
          }}
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("message", evt.target.value)}
          className="nodrag nowheel resize-none bg-white min-h-32 max-h-48 border-red-200/80"
          placeholder="Thank you, Have a wonderful day"
        />

        <div className="mt-2 text-[10px] text-red-700/60 px-1">
          <div>• Message will be played via TTS</div>
          <div>• Call ends immediately after</div>
          <div>• No user response expected</div>
        </div>
      </div>

      {/* No transitions - hang up is a terminal node */}
      <div className="text-[10px] px-3 pb-2 text-red-600/70 font-medium">
        Terminal Node (No Transitions)
      </div>
    </div>
  );
};

export default React.memo(HangUp, (prevProps, nextProps) => {
  // Only re-render if id or data changes, ignore selected
  return (
    prevProps.id === nextProps.id &&
    prevProps.data === nextProps.data
  );
});
